import { useModals } from '@/components/modals/ModalProvider';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import { usePrintPurchaseOrder } from '@/libs/fleet-api/hooks/purchase-order';
import {
	MonitoringSLADetailParam,
	useMonitoringSLADetail,
} from '@/libs/fleet-api/services/monitoring';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Flex, Table, message, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

type MonitoringSLAPageProps = MonitoringSLADetailParam & {
	onBack?: () => void;
};

const MonitoringSLAPage: FC<MonitoringSLAPageProps> = ({
	prosectId,
	purchaseOrderId,
	mppId,
	spfId,
	module,
	onBack,
}) => {
	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openPreviewDocumentModal,
	} = useModals();

	const printPurchaseOrderMutation = usePrintPurchaseOrder();
	const monitoringSLADetailQuery = useMonitoringSLADetail({
		prosectId,
		purchaseOrderId,
		module,
		mppId,
		spfId,
	});

	const {
		customerId,
		customerName,
		prospectId,
		bmppGroupId,
		bmppGroupName,
		orderSource,
		divisionName,
		plafonType,
		fbdhName,
		frmName,
		purchaseOrderNo,
		mppNo,
		approvalHistories,
	} = monitoringSLADetailQuery.data?.data ?? {};

	const handleDownloadExcel = () => {
		try {
			if (!approvalHistories || approvalHistories.length === 0) {
				message.warning('Tidak ada data untuk diunduh');
				return;
			}

			// Prepare data for Excel export
			const excelData: {
				'No': number | string;
				'Name': string;
				'Date': string;
				'Duration': string;
				'Process': string;
				'Status': string;
				'Detail Process': string;
				'Approval Notes': string;
			}[] = approvalHistories.map((record, index) => ({
				'No': index + 1,
				'Name': record.name || '-',
				'Date': record.date ? dayjs(record.date).format('DD/MM/YYYY') : '-',
				'Duration': record.duration || '-',
				'Process': record.process || '-',
				'Status': record.status || '-',
				'Detail Process': record.processDetail || '-',
				'Approval Notes': record.notes || '-',
			}));

			// Add summary row
			if (approvalHistories.length > 0) {
				const firstRecord = approvalHistories[0];
				const lastRecord = approvalHistories[approvalHistories.length - 1];

				const minDate = firstRecord.startDateTime
					? dayjs(firstRecord.startDateTime)
					: dayjs(firstRecord.date);
				const maxDate = lastRecord.endDateTime
					? dayjs(lastRecord.endDateTime)
					: dayjs(lastRecord.date);

				const totalDurationSeconds = Math.ceil(
					maxDate.diff(minDate, 'second', true),
				);
				const totalDuration = formatDuration(totalDurationSeconds);
				const dateRange = `${minDate.format('DD/MM/YYYY - HH:mm:ss')} - ${maxDate.format('DD/MM/YYYY - HH:mm:ss')}`;

				excelData.push({
					'No': 'Total',
					'Name': '',
					'Date': dateRange,
					'Duration': totalDuration,
					'Process': '',
					'Status': '',
					'Detail Process': '',
					'Approval Notes': '',
				});
			}

			// Create worksheet
			const worksheet = XLSX.utils.json_to_sheet(excelData);

			// Set column widths for better readability
			const columnWidths = [
				{ wch: 5 }, // No
				{ wch: 20 }, // Name
				{ wch: 12 }, // Date
				{ wch: 15 }, // Duration
				{ wch: 20 }, // Process
				{ wch: 15 }, // Status
				{ wch: 20 }, // Detail Process
				{ wch: 25 }, // Approval Notes
			];
			worksheet['!cols'] = columnWidths;

			// Create workbook
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, 'SLA Detail');

			// Generate filename with current date
			const currentDate = dayjs().format('YYYYMMDD');
			const filename = `SLA_Detail_${customerName || 'Customer'}_${currentDate}.xlsx`;

			// Write file
			const excelBuffer = XLSX.write(workbook, {
				bookType: 'xlsx',
				type: 'array',
			});
			const data = new Blob([excelBuffer], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});

			saveAs(data, filename);
			message.success('File berhasil diunduh');
		} catch (error) {
			console.error('Error downloading Excel file:', error);
			message.error('Gagal mengunduh file');
		}
	};

	const formatDuration = (totalSeconds: number): string => {
		const hours = Math.floor(totalSeconds / 3600);
		const remainingSeconds = totalSeconds % 3600;
		const minutes = Math.floor(remainingSeconds / 60);
		const seconds = Math.floor(remainingSeconds % 60);

		return `${hours.toString().padStart(2, '0')}:${minutes
			.toString()
			.padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
	};

	// Calculate summary data
	const summaryData = (() => {
		if (!approvalHistories || approvalHistories.length === 0) return null;

		const firstRecord = approvalHistories[0];
		const lastRecord = approvalHistories[approvalHistories.length - 1];

		const minDate = firstRecord.startDateTime
			? dayjs(firstRecord.startDateTime)
			: dayjs(firstRecord.date);
		const maxDate = lastRecord.endDateTime
			? dayjs(lastRecord.endDateTime)
			: dayjs(lastRecord.date);

		const totalDurationSeconds = Math.ceil(
			maxDate.diff(minDate, 'second', true),
		);

		return {
			dateRange: (
				<>
					{minDate.format('DD/MM/YYYY - HH:mm:ss')}
					<br />
					{maxDate.format('DD/MM/YYYY - HH:mm:ss')}
				</>
			),
			totalDuration: formatDuration(totalDurationSeconds),
		};
	})();

	return (
		<Fragment>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 20,
				}}
			>
				{typeof onBack === 'function' && (
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => onBack?.()}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
				)}
				<div style={{ flex: 1 }}>
					<FleetTitle>Detail SLA</FleetTitle>
				</div>
			</div>

			<DataRow
				loading={monitoringSLADetailQuery.isFetching}
				items={[
					[
						[
							'Nama Customer',
							customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(customerId);
									}}
								>
									{customerName}
								</a>
							) : (
								customerName ?? '-'
							),
						],
						[
							'Group Customer BMPP',
							bmppGroupId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(bmppGroupId);
									}}
								>
									{bmppGroupName}
								</a>
							) : (
								bmppGroupName ?? '-'
							),
						],
					],
					[
						['FRM', frmName ?? '-'],
						['FBDH ', fbdhName ?? '-'],
					],
					[
						['Divisi', divisionName ?? '-'],
						['Jenis Plafon', plafonType ?? '-'],
					],
					[
						['Sumber Order', orderSource ?? '-'],
						module === 'application'
							? ['Prospect ID', prospectId ?? '-']
							: ['No MPP', mppNo ?? '-'],
					],
					module === 'disbursement'
						? [
								[
									'No PO',
									purchaseOrderId ? (
										<a
											href="#"
											onClick={async (e) => {
												e.preventDefault();
												const documents =
													await printPurchaseOrderMutation.mutateAsync({
														purchaseOrderIds: [purchaseOrderId],
													});

												openPreviewDocumentModal({
													url: documents.map((d) => d.url),
													allowDownload: true,
												});
											}}
										>
											{purchaseOrderNo}
										</a>
									) : (
										purchaseOrderNo ?? '-'
									),
								],
							]
						: undefined,
				]}
			/>

			<Table
				style={{ marginBlock: 20 }}
				bordered
				scroll={{ x: 'max-content' }}
				loading={monitoringSLADetailQuery.isFetching}
				dataSource={approvalHistories?.map((record, key) => {
					return {
						...record,
						key,
					};
				})}
				pagination={false}
				summary={() => {
					if (!summaryData) return undefined;
					return (
						<Table.Summary.Row
							style={{ background: '#FFFBE6', fontWeight: 'bold' }}
						>
							<Table.Summary.Cell
								index={1}
								align="center"
								colSpan={2}
							>
								Total
							</Table.Summary.Cell>
							<Table.Summary.Cell
								index={2}
								align="center"
							>
								{summaryData.dateRange}
							</Table.Summary.Cell>
							<Table.Summary.Cell
								index={3}
								align="center"
							>
								{summaryData.totalDuration}
							</Table.Summary.Cell>
							<Table.Summary.Cell
								index={4}
								colSpan={4}
							/>
						</Table.Summary.Row>
					);
				}}
				columns={[
					{
						title: 'No',
						align: 'center',
						fixed: 'left',
						width: 1,
						render: (_, __, index) => index + 1,
					},
					{
						title: 'Name',
						render: (_, record) => record.name ?? '-',
					},
					{
						title: 'Date',
						align: 'center',
						render: (_, record) =>
							record.date ? (
								<Tooltip
									title={
										<div>
											<div>
												start:{' '}
												{record.startDateTime
													? dayjs(record.startDateTime).format(
															'DD/MM/YYYY - HH:mm:ss',
														)
													: '-'}
											</div>
											<div>
												end:{' '}
												{record.endDateTime
													? dayjs(record.endDateTime).format(
															'DD/MM/YYYY - HH:mm:ss',
														)
													: '-'}
											</div>
											<div>start process: {record.startProcess ?? '-'}</div>
											<div>end process: {record.endProcess ?? '-'}</div>
										</div>
									}
								>
									{dayjs(record.date).format('DD/MM/YYYY')}
									<br />
									<small>{dayjs(record.date).format('HH:mm:ss')}</small>
								</Tooltip>
							) : (
								'-'
							),
					},
					{
						title: 'Duration',
						align: 'center',
						render: (_, record) => <pre>{record.duration ?? '-'}</pre>,
					},
					{
						title: 'Process',
						align: 'center',
						render: (_, record) => record.process ?? '-',
					},
					{
						title: 'Status',
						align: 'center',
						render: (_, record) => record.status ?? '-',
					},
					{
						title: 'Detail Process',
						align: 'center',
						render: (_, record) => record.processDetail ?? '-',
					},
					{
						title: 'Approval Notes',
						render: (_, record) => record.notes ?? '-',
					},
				]}
			/>

			<Flex justify="end">
				<Button
					type="primary"
					onClick={handleDownloadExcel}
				>
					Download
				</Button>
			</Flex>
		</Fragment>
	);
};

export default MonitoringSLAPage;
